import { FC, useState, useEffect } from 'react';
import {
  Button, Caption,
  Cell,
  Input, Select,
  Textarea
} from '@telegram-apps/telegram-ui';
import { ImageCropDialog } from '@/components/ImageCropDialog.tsx';
import { uploadImage, createSecretSantaGame, updateSecretSantaGame } from '@/api/apiMethods.ts';
import {SecretSantaGame, Currencies, Currency} from '@/api/types.ts';
import { TbTextPlus, TbCalendar, TbCoin } from 'react-icons/tb';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import { useTranslation } from 'react-i18next';
import { mainButton } from '@telegram-apps/sdk-react';
import { setOnClickMainButton } from '@/helpers/tgButtons.ts';
import { ModalDatePicker } from '@/components/ModalDatePicker/ModalDatePicker.tsx';
import { haptic } from '@/helpers/haptic.ts';

interface SecretSantaGameFormProps {
  game?: SecretSantaGame;
  onSaved?: (savedGame: SecretSantaGame) => void;
}

export const SecretSantaGameForm: FC<SecretSantaGameFormProps> = ({ game, onSaved }) => {
  const [imageToCrop, setImageToCrop] = useState<File | null>(null);
  const [coverImageId, setCoverImageId] = useState<string | null>(game?.cover_image_id || null);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(true);
  const [title, setTitle] = useState<string>(game?.title || '');
  const [titleValidateError, setTitleValidateError] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(game?.description || '');
  const [budgetAmount, setBudgetAmount] = useState<string>(game?.budget?.number?.toString() || '');
  const [budgetCurrency, setBudgetCurrency] = useState<string>(game?.budget?.currency || 'USD');
  const [eventDate, setEventDate] = useState<Date | null>(game?.event_date || new Date(new Date().getFullYear(), 11, 31));
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);
  const { showSnackbar } = useNavigation();
  const { t, i18n: { language } } = useTranslation();

  const saveGame = async () => {
    if (!title) {
      haptic.notification('error');
      setTitleValidateError(true);
      showSnackbar('❌', 'system.emptyTitle');
      return;
    }

    setIsSaveInProgress(true);
    const gameInfo = {
      title,
      description: description || null,
      budget: budgetAmount ? { number: parseFloat(budgetAmount), currency: budgetCurrency } : null,
      event_date: eventDate,
      cover_image_id: coverImageId,
    };

    try {
      if (game) {
        const updatedGame = await updateSecretSantaGame(game.id, gameInfo);
        if (onSaved) {
          onSaved({ ...game, ...updatedGame });
        }
      } else {
        const createdGame = await createSecretSantaGame(gameInfo);
        if (onSaved) {
          onSaved(createdGame);
        }
      }
      haptic.notification('success');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', game ? 'secretSanta.error.updateFailed' : 'secretSanta.error.createFailed');
    }

    setIsSaveInProgress(false);
  };

  useEffect(() => {
    setOnClickMainButton(async () => {
      await saveGame();
    });
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isSaveInProgress,
      isEnabled: !isSaveInProgress && isImageLoaded,
      text: !game ? t('secretSanta.create') : t('action.save'),
    });
  }, [saveGame, isSaveInProgress, isImageLoaded]);

  // const onSelectImage = async (event: ChangeEvent<HTMLInputElement>) => {
  //   const target = event.target as HTMLInputElement;
  //   if (!target.files) return;
  //   const file = target.files[0];
  //   if (!file) return;
  //   setImageToCrop(file);
  // };

  const onCropped = async (fileBlob: Blob) => {
    setCoverImageId(null);
    setIsImageLoaded(false);
    const file = new File([fileBlob], 'secret-santa', { type: fileBlob.type });
    const newImage = await uploadImage(file);
    setCoverImageId(newImage.id);
    setImageToCrop(null);
    setIsImageLoaded(true);
    haptic.notification('success');
  };

  const formatDateString = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="flex flex-col gap-3">
      {imageToCrop && (
        <ImageCropDialog
          onCrop={onCropped}
          file={imageToCrop}
          onClose={() => setImageToCrop(null)}
        />
      )}

      {/*<div className="flex flex-col items-center gap-0">*/}
      {/*  <div className="p-3 min-w-20 ml-5 mr-5 max-h-32 overflow-hidden flex justify-center items-center relative">*/}
      {/*    {coverImageId ? (*/}
      {/*      <div className="w-full h-32 bg-section rounded-lg flex items-center justify-center">*/}
      {/*        {!isImageLoaded && <Spinner className="image-loader" size="l" />}*/}
      {/*      </div>*/}
      {/*    ) : (*/}
      {/*      <Image size={96} style={{ backgroundColor: '#00000010' }} fallbackIcon={<TbCamera size={50} />} />*/}
      {/*    )}*/}
      {/*  </div>*/}

      {/*  <div style={{ marginTop: '-15px', marginBottom: '-15px' }}>*/}
      {/*    <FileInput*/}
      {/*      label={t('secretSanta.form.coverImage')}*/}
      {/*      onChange={onSelectImage}*/}
      {/*      accept="image/*,capture=camera"*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*</div>*/}

      <Input
        placeholder={t('secretSanta.form.title')}
        before={<TbTextPlus size={20} />}
        onFocus={() => setTitleValidateError(false)}
        onBlur={() => setTitleValidateError(!title)}
        status={titleValidateError ? 'error' : 'default'}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Textarea
        placeholder={t('secretSanta.form.description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div className="flex gap-2">
        <Input
          placeholder={t('secretSanta.form.budget')}
          before={<TbCoin size={20} />}
          value={budgetAmount}
          onChange={(e) => setBudgetAmount(e.target.value)}
          type="number"
          className="flex-1"
        />
      <Select
          onChange={(e) => {
            haptic.selection();
            setBudgetCurrency(e.target.value);
          }}
          defaultValue={budgetCurrency}>
          {Currencies.map((currency:Currency) => (
            <option key={"currency"+currency.code} value={currency.code}>{currency.symbol}</option>
          ))}
        </Select>
      </div>

      <Cell
        before={<TbCalendar size={20} />}
        onClick={() => {
          haptic.impact('light');
          setIsDatePickerOpen(true);
        }}
        after={eventDate ?
          <Caption className={"tg-text-button-color"} weight="1">{formatDateString(eventDate)}</Caption> :
          <Caption weight="1">{t('secretSanta.form.eventDate')}</Caption>
        }
      >
        <Caption weight="2">{t('secretSanta.form.eventDate')}</Caption>
      </Cell>

      <ModalDatePicker
        initialDate={eventDate}
        opened={isDatePickerOpen}
        onApply={async (date) => {
          setEventDate(date);
          setIsDatePickerOpen(false);
        }}
        onCancel={() => {
          setIsDatePickerOpen(false);
        }}
        maxDate={new Date(2099, 0, 1)}
        title={t('secretSanta.form.eventDate')}
      />

      {import.meta.env.MODE === 'development' && (
        <Button disabled={isSaveInProgress} loading={isSaveInProgress} onClick={saveGame} stretched={true}>
          {!game ? t('secretSanta.create') : t('action.save')}
        </Button>
      )}
    </div>
  );
};
